import Box from '@mui/material/Box';
import { PieChart, PieChartProps } from '@mui/x-charts/PieChart';
import { theme } from '@store';
import { Typography } from '@mui/material';
import { formatCount } from '@util/number';
import { useDonutChart } from './hooks';
import { ChartEntry } from '@views/pages/Dashboard/types';
import { ChartNoResultPlaceholder } from '@views/pages/Dashboard/components';

const LegendSquare = ({ color }: { color: string }) => (
  <svg width='20' height='20' viewBox='0 0 20 20'>
    <rect width='20' height='20' fill={color} rx='2' />
  </svg>
);

type IDonutChartProps = {
  entries: ChartEntry[];
  // the n-th item descending by value from which all the entries cumulate into one
  otherThreshold?: number;
  startColor?: string;
  endColor?: string;
  /** Sorts entries by value descending by default */
  preserveOrder?: boolean;
} & Omit<PieChartProps, 'series'>;

export const DonutChart = ({
  entries,
  otherThreshold = 5,
  startColor = theme.palette.primary.main,
  endColor = theme.palette.emerald.main,
  height = 240,
  width = 240,
  preserveOrder = false,
  ...rest
}: IDonutChartProps) => {
  const { colors, finalEntries, totalValue } = useDonutChart({
    entries,
    startColor,
    endColor,
    otherThreshold,
    preserveOrder,
  });

  return totalValue <= 0 ? (
    <ChartNoResultPlaceholder />
  ) : (
    <Box sx={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: 200,
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        }}
      >
        <Typography variant='h4'>{formatCount(totalValue)}</Typography>
      </Box>
      <Box
        sx={{
          gap: '3.5rem',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <PieChart
            height={height}
            width={width}
            colors={colors}
            series={[
              {
                labelMarkType: 'square',
                innerRadius: 70,
                data: finalEntries.map(entry => ({
                  ...entry,
                  label: entry.label,
                })),
                valueFormatter: item =>
                  `${formatCount(item.value)} (${((100 * item.value) / totalValue).toFixed(1)}%)`,
              },
            ]}
            slotProps={{
              legend: {
                sx: {
                  display: 'none',
                },
              },
            }}
            {...rest}
          />
          <Typography
            variant='h4'
            sx={{
              [theme.breakpoints.up('md')]: {
                display: 'none',
              },
            }}
          >
            {formatCount(totalValue)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '0.5rem',
            minWidth: 340,
            [theme.breakpoints.down('sm')]: {
              minWidth: 190,
            },
          }}
        >
          {finalEntries.map((entry, index) => (
            <Box
              key={`${entry.label} - ${entry.value} - ${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 'fit-content',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LegendSquare color={colors[index]} />
                <Typography variant='body2' sx={{ marginLeft: 1 }}>
                  {entry.label}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                }}
              >
                <Typography
                  variant='subtitle2'
                  sx={{
                    lineHeight: 1,
                  }}
                >
                  {formatCount(entry.value)}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {((100 * entry.value) / totalValue).toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
